import { useEffect, useMemo, useRef, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { DatePicker } from '../Common/DatePicker'
import { AppDialog } from '../Common/AppDialog'
import { StatusChip } from '../Common'
import { useWorkspace } from '../../context/useWorkspace'
import { useTimeline } from '../../context/useTimeline'
import { useNls } from '../../nls/nlsProvider'
import { useChecklistDetailController } from '../../hooks/useChecklistDetailController'
import { normalizeChecklistInputLimits } from '../../utils/checklistLimits'
import { isPersonalWorkspace } from '../../utils/workspaceKind'
import { resolveDefaultBreadcrumb, type ChecklistBreadcrumbSegment } from '../../utils/checklistBreadcrumb'
import { ChecklistDetailControllerDrawer } from './ChecklistDetailControllerDrawer'
import { dateFromIsoWeekKey, isoDate, subscribeToBookPages } from '../../services/checklistBookService'
import { ChecklistPanel, type ChecklistPanelSection } from './ChecklistPanel'
import {
  ChecklistNodeCoreFields,
  ChecklistNodeRiskSection,
} from '../DrawingBoard/ChecklistNodeDetailFields'
import { getChecklistOccurrenceTodayLabel } from './ChecklistNls'
import type { ChecklistItem, RecurringOccurrenceState } from '../../types/schema'
import { bookIdFor, type ChecklistBookPage } from '../../types/checklistBooks'
import {
  currentOccurrenceKeyForItem,
  formatOccurrenceTitle,
  occurrenceKeyFor,
  parseIsoDate,
} from '../../services/recurringChecklistService'
import type { StatusPillTone } from '../Common/Chip'
import {
  buildRecurringOccurrenceEntries,
  formatRecurringOccurrenceRangeSummary,
  groupRecurringOccurrenceEntries,
  occurrenceStatusChipKind,
  occurrenceStatusChipLabel,
} from './recurringOccurrenceUtils'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'

const CHIP_KIND_TONE_MAP: Record<'active' | 'done' | 'archived' | 'upcoming' | 'past', StatusPillTone> = {
  active: 'info',
  done: 'success',
  archived: 'muted',
  upcoming: 'neutral',
  past: 'neutral',
}

export type ChecklistDetailDraft = Partial<ChecklistItem> & {
  id: string
  title: string
  parentContainerId: string
  workspaceId: string
  isDraft: true
}

type Props = {
  open: boolean
  onDismiss: () => void
  presentation?: 'modal' | 'embedded'
  /** Either an existing item id (looks it up via the detail controller) or
   *  a draft object (new-item wizard flow). */
  itemId?: string | null
  draft?: ChecklistDetailDraft | null
  mode?: 'view' | 'edit'
  onSaveDraft?: (item: Partial<ChecklistItem>) => Promise<void> | void
  onDiscardDraft?: () => void
  onOpenInCanvas?: (itemId: string) => void
  /** Breadcrumb reflecting where the user opened the dialog from (Board /
   *  Manage / Canvas etc.). When absent, a fallback path is derived from
   *  the item's parent chain. */
  breadcrumb?: ChecklistBreadcrumbSegment[]
  draftEyebrow?: string
  draftSaveLabel?: string
  draftFooterHint?: string
  initialOccurrenceKey?: string | null
  upcomingWindow?: number
}

/**
 * ChecklistDetailDialog — portal-based dialog that surfaces the full
 * checklist details popup on its right pane, plus a recurring-occurrence
 * sidebar on the left when the item is DAILY / WEEKLY. Master (cover) data
 * is the same for every occurrence; only completion state swaps datewise
 * via `focusedOccurrenceKey` on the embedded NodeDetailDrawer. The right
 * pane reuses the same section renderer as every other checklist popup
 * (see `NodeDetailDrawer` + `CHECKLIST_DETAIL_SECTIONS`) so both surfaces
 * stay in visual + structural sync.
 */
export const ChecklistDetailDialog = ({
  open,
  onDismiss,
  presentation = 'modal',
  itemId,
  draft,
  mode = 'view',
  onSaveDraft,
  onDiscardDraft,
  onOpenInCanvas,
  breadcrumb,
  draftEyebrow,
  draftSaveLabel,
  draftFooterHint,
  initialOccurrenceKey = null,
}: Props) => {
  const isDraft = Boolean(draft)
  const isEmbedded = presentation === 'embedded'
  const { t } = useNls()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const { checklistItems, elements } = useTimeline()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  const checklistLimits = normalizeChecklistInputLimits(
    activeWorkspace?.checklistInputLimits,
    isPersonalWorkspace(activeWorkspace),
  )

  const baseItem = useMemo(() => {
    if (draft) return draft as unknown as ChecklistItem
    if (!itemId) return null
    return checklistItems.find((entry) => entry.id === itemId) ?? null
  }, [draft, itemId, checklistItems])

  const recurringMode: 'DAILY' | 'WEEKLY' | null = useMemo(() => {
    const t = baseItem?.checklistType
    return t === 'DAILY' || t === 'WEEKLY' ? t : null
  }, [baseItem])
  const { timeZone } = useWorkspaceDay(baseItem?.workspaceId)
  const currentKey = useMemo(
    () => (baseItem && recurringMode ? currentOccurrenceKeyForItem(baseItem, new Date(), timeZone) : null),
    [baseItem, recurringMode, timeZone],
  )
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState<string | null>(null)
  const [occurrencePages, setOccurrencePages] = useState<ChecklistBookPage[]>([])
  const occurrenceRowRefs = useRef(new Map<string, HTMLLIElement | null>())
  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedOccurrenceKey(initialOccurrenceKey ?? currentKey)
  }, [open, currentKey, initialOccurrenceKey, baseItem?.id])
  const activeOccurrenceKey = selectedOccurrenceKey ?? currentKey

  // Only wire the controller when we're anchored to a real item id — the
  // draft-wizard flow never has one, and useChecklistDetailController would
  // fire empty subscriptions.
  const controller = useChecklistDetailController(itemId ?? '', {
    pageKey: recurringMode ? activeOccurrenceKey : null,
  })
  const resolvedItem = isDraft
    ? (draft as unknown as ChecklistItem | null)
    : controller.item ?? baseItem

  useEffect(() => {
    if (!open || !baseItem || !recurringMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOccurrencePages([])
      return
    }
    const bookId = bookIdFor(baseItem.workspaceId, recurringMode)
    return subscribeToBookPages(bookId, setOccurrencePages)
  }, [open, baseItem, recurringMode])

  const occurrenceEntries = useMemo(
    () => buildRecurringOccurrenceEntries(resolvedItem, occurrencePages, new Date(), undefined, timeZone),
    [occurrencePages, recurringMode, resolvedItem, timeZone],
  )
  const todayKey = recurringMode ? currentKey : null
  const minOccurrenceKey = occurrenceEntries[0]?.key ?? todayKey
  const maxOccurrenceKey = occurrenceEntries[occurrenceEntries.length - 1]?.key ?? todayKey
  const occurrencePickerValue = recurringMode === 'WEEKLY'
    ? activeOccurrenceKey ? isoDate(dateFromIsoWeekKey(activeOccurrenceKey)) : ''
    : activeOccurrenceKey ?? ''
  const minOccurrencePickerValue = recurringMode === 'WEEKLY'
    ? minOccurrenceKey ? isoDate(dateFromIsoWeekKey(minOccurrenceKey)) : undefined
    : minOccurrenceKey ?? undefined
  const maxOccurrencePickerValue = recurringMode === 'WEEKLY'
    ? maxOccurrenceKey ? isoDate(dateFromIsoWeekKey(maxOccurrenceKey)) : undefined
    : maxOccurrenceKey ?? undefined
  const occurrenceGroups = useMemo(
    () => groupRecurringOccurrenceEntries(occurrenceEntries),
    [occurrenceEntries],
  )
  const occurrenceRangeSummary = useMemo(
    () => formatRecurringOccurrenceRangeSummary(occurrenceEntries, activeOccurrenceKey),
    [activeOccurrenceKey, occurrenceEntries],
  )

  useEffect(() => {
    if (!open || occurrenceEntries.length === 0) return
    if (activeOccurrenceKey && occurrenceEntries.some((entry) => entry.key === activeOccurrenceKey)) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedOccurrenceKey(currentKey ?? occurrenceEntries[0]?.key ?? null)
  }, [activeOccurrenceKey, currentKey, occurrenceEntries, open])

  useEffect(() => {
    if (!open || !activeOccurrenceKey) return
    const frame = window.requestAnimationFrame(() => {
      occurrenceRowRefs.current.get(activeOccurrenceKey)?.scrollIntoView({ block: 'nearest' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [activeOccurrenceKey, open, occurrenceEntries.length])

  useEffect(() => {
    if (isEmbedded) return
    if (!open) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isEmbedded, open])

  if (!open) return null

  if (!resolvedItem && !isDraft && controller.status === 'loading') {
    return (
      <AppDialog
        open
        size="sm"
        actions={[]}
        onDismiss={onDismiss}
        title={t('NLSChecklistDetailLoadingItem')}
      >
        <div className="chkDetailDialog__missing">
          <p>{t('NLSChecklistDetailLoadingItem')}</p>
          <Button variant="secondary" size="sm" onClick={onDismiss}>{t('NLSChecklistActionClose')}</Button>
        </div>
      </AppDialog>
    )
  }

  if (!resolvedItem) {
    return (
      <AppDialog
        open
        size="sm"
        actions={[]}
        onDismiss={onDismiss}
        title={t('NLSChecklistDetailUnavailable')}
      >
        <div className="chkDetailDialog__missing">
          <p>{t('NLSChecklistDetailUnavailable')}</p>
          <Button variant="secondary" size="sm" onClick={onDismiss}>{t('NLSChecklistActionClose')}</Button>
        </div>
      </AppDialog>
    )
  }

  const focusedOccurrenceLabel = recurringMode && activeOccurrenceKey
    ? formatOccurrenceTitle(resolvedItem, activeOccurrenceKey)
    : null
  const resolvedBreadcrumb: ChecklistBreadcrumbSegment[] = breadcrumb && breadcrumb.length > 0
    ? breadcrumb
    : resolveDefaultBreadcrumb(resolvedItem, elements, { focusedOccurrenceLabel })

  const occurrenceMeta = (
    kind: 'archived' | 'past' | 'current' | 'upcoming',
    state?: RecurringOccurrenceState | null,
  ) => {
    if (kind === 'archived') {
      return state?.completedByName
        ? t('NLSChecklistOccurrenceCompletedByArchived', { name: state.completedByName })
        : t('NLSChecklistOccurrenceArchived')
    }
    if (kind === 'past') {
      return state?.completedByName
        ? t('NLSChecklistOccurrenceCompletedBy', { name: state.completedByName })
        : t('NLSChecklistOccurrenceNotCompleted')
    }
    if (kind === 'current') {
      return state?.completedAt
        ? t('NLSChecklistOccurrenceCompleted', {
          time: new Date(state.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        })
        : t('NLSChecklistDetailActive')
    }
    return t('NLSChecklistOccurrenceUpcoming')
  }

  const cardContent = (
    <>
      {!isEmbedded && (
        <IconButton
          variant="tertiary"
          className="chkDetailDialog__closeFloat"
          onClick={onDismiss}
          label={t('NLSChecklistActionClose')}
          icon={<CloseRoundedIcon fontSize="inherit" />}
        />
      )}
      {isDraft && (
        <header className="chkDetailDialog__draftBar">
          <span className="chkDetailDialog__eyebrow">{draftEyebrow ?? t('NLSChecklistDetailDraftBar')}</span>
          <h2 className="chkDetailDialog__title">
            {resolvedItem.title || <span style={{ opacity: 0.6 }}>{t('NLSChecklistDetailUntitled')}</span>}
          </h2>
        </header>
      )}

      <div className={`chkDetailDialog__body${recurringMode ? ' chkDetailDialog__body--withSidebar' : ''}`}>
        {recurringMode && occurrenceEntries.length > 0 && (
          <aside className="chkDetailDialog__occSidebar" aria-label={t('NLSChecklistDetailRecurrenceInstances')}>
            <header className="chkDetailDialog__occSidebarHeader">
              <span className="chkDetailDialog__occSidebarTitle">
                <RepeatRoundedIcon fontSize="inherit" />
                <strong>{t('NLSChecklistDetailRecurrenceInstances')}</strong>
                <small>{occurrenceRangeSummary}</small>
              </span>
              <DatePicker
                className="chkDetailDialog__occJumpPicker"
                size="sm"
                value={occurrencePickerValue}
                min={minOccurrencePickerValue}
                max={maxOccurrencePickerValue}
                clearable={false}
                selectionMode={recurringMode === 'WEEKLY' ? 'week' : 'day'}
                placement="bottom-start"
                todayLabel={getChecklistOccurrenceTodayLabel(t, recurringMode)}
                onChange={(raw) => {
                  if (!raw) return
                  const pickedKey = recurringMode === 'WEEKLY'
                    ? occurrenceKeyFor('WEEKLY', parseIsoDate(raw))
                    : raw
                  setSelectedOccurrenceKey(
                    occurrenceEntries.find((entry) => entry.key === pickedKey)?.key
                    ?? todayKey
                    ?? null,
                  )
                }}
                ariaLabel={recurringMode === 'DAILY' ? t('NLSChecklistAriaSpecificDate') : t('NLSChecklistAriaSpecificWeek')}
              />
            </header>
            <div className="chkDetailDialog__occList" role="list">
              {occurrenceGroups.map((group) => {
                const GroupIcon = group.id === 'past'
                  ? HistoryRoundedIcon
                  : group.id === 'today'
                    ? EventRoundedIcon
                    : ScheduleRoundedIcon
                return (
                  <section key={group.id} className={`chkDetailDialog__occGroup is-${group.id}`} aria-label={`${group.label} instances`}>
                    <h4 className="chkDetailDialog__occGroupHeader">
                      <GroupIcon fontSize="inherit" />
                      <span>{group.label}</span>
                      <em>{group.entries.length}</em>
                    </h4>
                    <ol className="chkDetailDialog__occGroupList">
                      {group.entries.map((row) => {
                        const isActive = row.key === activeOccurrenceKey
                        const chipKind = occurrenceStatusChipKind(row, isActive)
                        const glyph = row.state?.isCompleted
                          ? <CheckCircleRoundedIcon fontSize="inherit" />
                          : row.kind === 'archived'
                            ? <ArchiveRoundedIcon fontSize="inherit" />
                            : row.kind === 'past'
                              ? <RadioButtonUncheckedRoundedIcon fontSize="inherit" />
                              : row.kind === 'current'
                                ? <EventRoundedIcon fontSize="inherit" />
                                : <ScheduleRoundedIcon fontSize="inherit" />
                        return (
                          <li
                            key={row.key}
                            ref={(element) => { occurrenceRowRefs.current.set(row.key, element) }}
                            className={`chkDetailDialog__occItem is-${row.kind}${isActive ? ' is-active' : ''}${row.state?.isCompleted ? ' is-done' : ''}`}
                          >
                            <Pressable
                              onClick={() => setSelectedOccurrenceKey(row.key)}
                              aria-pressed={isActive}
                            >
                              <span className="chkDetailDialog__occGlyph" aria-hidden>{glyph}</span>
                              <span className="chkDetailDialog__occText">
                                <span className="chkDetailDialog__occKey">
                                  {row.label}
                                  <StatusChip size="sm" tone={CHIP_KIND_TONE_MAP[chipKind]}>
                                    {occurrenceStatusChipLabel(row, isActive)}
                                  </StatusChip>
                                </span>
                                <span className="chkDetailDialog__occMeta">
                                  {occurrenceMeta(row.kind, row.state)}
                                </span>
                              </span>
                            </Pressable>
                          </li>
                        )
                      })}
                    </ol>
                  </section>
                )
              })}
            </div>
            <footer className="chkDetailDialog__occSidebarFooter">
              <HistoryRoundedIcon fontSize="inherit" />
              <span>{t('NLSChecklistDetailCompletionPerInstance')}</span>
            </footer>
          </aside>
        )}

        <div className="chkDetailDialog__bodyMain">
          {!isDraft && itemId ? (
            <ChecklistDetailControllerDrawer
              key={`${resolvedItem.id}:${recurringMode ? activeOccurrenceKey ?? 'current' : 'base'}:${mode}`}
              open
              controller={controller}
              onClose={onDismiss}
              presentation="inline"
              breadcrumb={resolvedBreadcrumb}
              focusedOccurrenceKey={recurringMode ? activeOccurrenceKey : null}
            />
          ) : (
            <DraftPanel
              draft={resolvedItem}
              limits={checklistLimits}
              onSaveDraft={onSaveDraft}
              onDiscardDraft={onDiscardDraft}
              onDismiss={onDismiss}
              eyebrow={draftEyebrow}
              saveLabel={draftSaveLabel}
              footerHint={draftFooterHint}
            />
          )}
        </div>
      </div>

      {!isEmbedded && !isDraft && onOpenInCanvas && resolvedItem?.id && (
        <footer className="chkDetailDialog__footer">
          <div className="chkDetailDialog__footerHint" />
          <div className="chkDetailDialog__footerActions">
            <Button variant="secondary" size="sm" onClick={() => onOpenInCanvas(resolvedItem.id)}>
              {t('NLSChecklistActionOpenInCanvas')} <OpenInNewRoundedIcon fontSize="inherit" style={{ verticalAlign: '-2px' }} />
            </Button>
          </div>
        </footer>
      )}
    </>
  )

  if (isEmbedded) {
    return (
      <section
        className="chkDetailDialog__embedded"
        aria-label={isDraft ? t('NLSChecklistDetailNewChecklist') : resolvedItem.title || t('NLSChecklistDetailChecklistDetails')}
      >
        <div className="chkDetailDialog__card chkDetailDialog__card--embedded">
          {cardContent}
        </div>
      </section>
    )
  }

  return (
    <AppDialog
      open
      size="lg"
      actions={[]}
      onDismiss={onDismiss}
      title={isDraft ? t('NLSChecklistDetailNewChecklist') : resolvedItem.title || t('NLSChecklistDetailChecklistDetails')}
    >
      <div className="chkDetailDialog__card">
        {cardContent}
      </div>
    </AppDialog>
  )
}

// ---------------------------------------------------------------------------
// Draft path — kept minimal for the new-item wizard flow. Existing items
// route to `ChecklistDetailControllerDrawer` above.
// ---------------------------------------------------------------------------

type DraftPanelProps = {
  draft: ChecklistItem
  limits: ReturnType<typeof normalizeChecklistInputLimits>
  onSaveDraft?: (item: Partial<ChecklistItem>) => Promise<void> | void
  onDiscardDraft?: () => void
  onDismiss: () => void
  eyebrow?: string
  saveLabel?: string
  footerHint?: string
}

const DraftPanel = ({
  draft,
  limits,
  onSaveDraft,
  onDiscardDraft,
  onDismiss,
  saveLabel,
  footerHint,
}: DraftPanelProps) => {
  const { t } = useNls()
  const [editing, setEditing] = useState<Partial<ChecklistItem>>({ ...draft })
  const [saving, setSaving] = useState(false)
  const editingDetailValues = { ...editing, weightage: editing.weightage ?? 1 }

  const patch = (updates: Partial<ChecklistItem>) => {
    setEditing((current) => ({ ...current, ...updates }))
  }

  const handleSave = async () => {
    if (!onSaveDraft) return
    try {
      setSaving(true)
      await onSaveDraft(editing)
      onDismiss()
    } finally {
      setSaving(false)
    }
  }

  const sections: ChecklistPanelSection[] = [
    {
      id: 'details',
      title: t('NLSChecklistDetailDetails'),
      icon: <ArticleRoundedIcon fontSize="inherit" />,
      pinnedOpen: true,
      body: (
        <>
          <ChecklistNodeCoreFields
            titleValue={editing.title || ''}
            values={editingDetailValues}
            limits={limits}
            disabled={false}
            onTitleChange={(next) => patch({ title: next })}
            onTitleCommit={() => undefined}
            onChange={patch}
          />
          <ChecklistNodeRiskSection
            nodeId={editing.id || 'draft'}
            values={editingDetailValues}
            limits={limits}
            disabled={false}
            wrapper="none"
            onChange={patch}
          />
        </>
      ),
    },
    {
      id: 'risk-hint',
      title: t('NLSChecklistDetailOptions'),
      icon: <TuneRoundedIcon fontSize="inherit" />,
      defaultOpen: false,
      body: <p style={{ opacity: 0.7 }}>{t('NLSChecklistDetailSaveUnlocksOptions')}</p>,
    },
  ]

  return (
    <>
      <ChecklistPanel
        variant="inline"
        density="cozy"
        accent="none"
        sections={sections}
        className="chkDetailDialog__panel"
      />
      <div className="chkDetailDialog__footer chkDetailDialog__footer--draft">
        <div className="chkDetailDialog__footerHint">
          {footerHint ?? t('NLSChecklistDetailDraftFooter')}
        </div>
        <div className="chkDetailDialog__footerActions">
          <Button variant="tertiary" size="sm" onClick={() => { onDiscardDraft?.(); onDismiss() }}>
            {t('NLSChecklistActionDiscard')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={saving}
            disabled={!editing.title?.trim()}
          >
            {saveLabel ?? t('NLSChecklistActionSave')}
          </Button>
        </div>
      </div>
    </>
  )
}
